import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Route, Routes, useNavigate, Navigate, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Breadcrumbs from "./Breadcrumbs";
import Login from "./Login";
import Callback from "./Callback";
import AuthError from "./AuthError";
import ProjectDetail from "../projects/ProjectDetail";
import ProjectDetails from "../projects/ProjectDetails";
import CampaignDetails from "../campaigns/CampaignDetails";
import CampaignDetailsProjects from "../campaigns/CampaignDetailsProjects";
import CampaignDetailsInfo from "../campaigns/CampaignDetailsInfo";
import CreateCampaign from "../campaigns/CreateCampaign";
import AddProject from "../campaigns/AddProject";
import InitialSetup from "../initialsetup/InitialSetup";
import FilterPage from "../filters/FilterPage";
import BulkUpload from "../bulkupload/BulkUpload";
import BulkUploadDetails from "../bulkupload/BulkUploadDetails";
import UserManagement from "../management/UserManagement";
import RoleMapping from "../initialsetup/RoleMapping";
import MappedHeaders from "../bulkupload/MappedHeaders";
import BuildFormula from "../filters/BuildFormula";
import GlobalTemplates from "../checklist/admin/GlobalTemplates";
import GlobalTemplatesDetails from "../checklist/admin/GlobalTemplatesDetails";
import AdminCheckListManagement from "../checklist/admin/AdminCheckListManagement";
import AuditorCheckListManagement from "../checklist/auditor/AuditorCheckListManagement";
import AdminViewChecklistDetails from "../checklist/admin/AdminViewCheckListDetails";
import AuditorViewChecklistDetails from "../checklist/auditor/AuditorViewCheckListDetails";
import AdminChecklistDetails from "../checklist/admin/AdminCheckListDetails";
import AuditorChecklistDetails from "../checklist/auditor/AuditorCheckListDetails";
import CustomTheme from "./Customtheme";
import ProjectHistory from "../projects/ProjectHistory";
import RoleSelectionModal from "./RoleSelectionModal";
import EmailTemplateUI from "../Email/EmailTemplateUI";
import CreateTemplate from "../Email/CreateTemplate";
import EditTemplate from "../Email/EditTemplate";
import Home from "../dashboard/Home";
import NCTracker from "../tracker/NCTracker";
import NCTrackerDetails from "../tracker/NCTrackerDetails";
import FunctionCampaign from "../functioncampaigns/FunctionCampaign";
import FunctionCampaignDetails from "../functioncampaigns/FunctionCampaignDetails";
import AddCampaignFunctions from "../functioncampaigns/AddCampaignFunctions";
import Functions from "../functions/Functions";
import CreateFunction from "../functions/CreateFunction";
import FunctionViewChecklistDetails from "../functionchecklist/admin/FunctionViewCheckListDetails";
import CreateFunctionCampaign from "../functioncampaigns/CreateFunctionCampaign";
import FunctionCampaignDetailsInfo from "../functioncampaigns/FunctionCampaignDetailsInfo";
import FunctionChecklist from "../functionchecklist/admin/FunctionChecklist";
import FunctionCheckListDetails from "../functionchecklist/admin/FunctionCheckListDetails";
import AuditorFunctionChecklist from "../functionchecklist/auditor/AuditorFunctionChecklist";
import AuditorFunctionCheckListDetails from "../functionchecklist/auditor/AuditorFunctionCheckListDetails";
import AuditorViewFunctionCheckListDetails from "../functionchecklist/auditor/AuditorViewFunctionCheckListDetails";
import FunctionNCTracker from "../tracker/FunctionNCTracker";
import FunctionNCTrackerDetails from "../tracker/FunctionNCTrackerDetails";
import AddFunctions from "../functions/AddFunctions";
import AdminReview from "../campaigns/AdminReview";
import { PersistRouteProps } from '../interFaces/interfaces';
import { apiCall, clearAuthCookie, checkAuthStatus } from "./ApiCall";
import GlobalFunctionTemplates from "../functionchecklist/admin/GlobalFunctionTemplates";
import GlobalFunctionTemplateDetails from "../functionchecklist/admin/GlobalFunctionTemplateDetails";
import EncryptionUtils from "./encryptionUtils";
import MeetingTracker from "../meetingtracker/MeetingTracker";
import MeetingTrackerDetails from "../meetingtracker/MeetingTrackerDetails";
import MeetingTrackerDetailsInfo from "../meetingtracker/MeetingTrackerDetailsInfo";
import FunctionMeetingTracker from "../meetingtracker/FunctionMeetingTracker";
import FunctionMeetingTrackerDetails from "../meetingtracker/FunctionMeetingTrackerDetails";
import FunctionMeetingTrackerDetailsInfo from "../meetingtracker/FunctionMeetingTrackerDetailsInfo";
import { AppProps } from '../interFaces/interfaces';
import "../css/main/App.css";
import CampaignAndProjectHistory from "../campaigns/CampaignAndProjectHistory";
import CampaignAndProjectHistoryDetails from "../campaigns/CampaignAndProjectHistoryDetails";
import ExcludedCampaignAndProject from "../campaigns/ExcludedCampaignAndProject";
import ExcludedCampaignAndProjectDetails from "../campaigns/ExcludedCampaignAndProjectDetails";
const AppContent: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [showRoleModal, setShowRoleModal] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [shouldBlurContent, setShouldBlurContent] = useState<boolean>(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsSidebarOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const performAuthCheck = useCallback(async () => {
    try {
      const isAuth = await checkAuthStatus();
      const roleId = EncryptionUtils.get("roleId");
      setIsAuthenticated(isAuth);
      if (isAuth && !roleId) {
        setShowRoleModal(true);
      } else {
        setShowRoleModal(false);
      }
    } catch (error) {
      setIsAuthenticated(false);
      setShowRoleModal(false);
    } finally {
      setIsCheckingAuth(false);
    }
  }, []);
  useEffect(() => {
    if (location.pathname !== "/auth/callback") {
      performAuthCheck();
    }
  }, [performAuthCheck, location.pathname]);
  useEffect(() => {
    if (location.pathname === "/") {
      performAuthCheck();
    }
  }, [location.pathname, performAuthCheck]);
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (isAuthenticated && location.pathname !== "/auth/callback") {
        performAuthCheck();
      }
    }, 30000);
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "authEvent") {
        const authEvent = JSON.parse(event.newValue || "{}");
        if (authEvent.type === "LOGOUT") {
          setIsAuthenticated(false);
          setShowRoleModal(false);
        } else if (authEvent.type === "LOGIN") {
          performAuthCheck();
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated && location.pathname !== "/auth/callback") {
        performAuthCheck();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener("storage", handleStorageChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, location.pathname, performAuthCheck]);
  const handleSidebarToggle = (isOpen: boolean) => {
    if (isMobile && isOpen) {
      setShouldBlurContent(true);
      document.body.style.overflow = 'hidden';
    } else {
      setShouldBlurContent(false);
      document.body.style.overflow = 'auto';
    }
  };
  const toggleSidebar = (): void => {
    setIsSidebarOpen((prev) => !prev);
  };
  const handleRoleSelected = () => {
    setShowRoleModal(false);
  };
  const handleLogout = async () => {
    try {
      await apiCall("post", "/api/auth/logout", {});
    } catch (error) {
      console.error("Logout API failed:", error);
    } finally {
      clearAuthCookie();
      setIsAuthenticated(false);
      localStorage.setItem("authEvent", JSON.stringify({ type: "LOGOUT" }));
      navigate("/login");
    }
  };
  if (isCheckingAuth && location.pathname !== "/auth/callback") {
    return <div className="auth-loading"></div>;
  }
  return (
    <>
      {isAuthenticated ? (
        <>
          {showRoleModal && (
            <RoleSelectionModal onRoleSelected={handleRoleSelected} setIsAuthenticated={setIsAuthenticated} />
          )}
          <PersistRoute isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} setIsAuthenticated={setIsAuthenticated} handleLogout={handleLogout} shouldBlurContent={shouldBlurContent} onSidebarToggle={handleSidebarToggle} isMobile={isMobile} />
        </>
      ) : (
        <Routes>
          <Route path="/login" element={<Login setIsAuthenticated={setIsAuthenticated} />} />
          <Route path="/auth/callback" element={<Callback setIsAuthenticated={setIsAuthenticated} />} />
          <Route path="/auth/error" element={<AuthError />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )}
    </>
  );
};
const App: React.FC<AppProps> = () => {
  const routerFutureFlags = { v7_startTransition: true, v7_relativeSplatPath: true };
  return (
    <Router future={routerFutureFlags}>
      <AppContent />
    </Router>
  );
};
const PersistRoute: React.FC<PersistRouteProps & { isMobile: boolean }> = ({ isSidebarOpen, toggleSidebar, setIsAuthenticated, handleLogout, shouldBlurContent, onSidebarToggle, isMobile }) => {
  if (!EncryptionUtils.get("roleId")) return null;
  const contentClass = `app-content ${shouldBlurContent ? 'blurred' : ''} ${isMobile ? 'mobile-content' : isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`;
  return (
    <div className="app-container">
      <Sidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} setIsAuthenticated={setIsAuthenticated} handleLogout={handleLogout} onSidebarToggle={onSidebarToggle} isMobile={isMobile} />
      {isMobile && !isSidebarOpen && (
        <button className="mobile-sidebar-toggle" onClick={toggleSidebar} aria-label="Open sidebar"><span>☰</span></button>
      )}
      <div className={contentClass} onClick={() => { if (shouldBlurContent) toggleSidebar(); }}>
        <Breadcrumbs handleLogout={handleLogout} />
        <Routes>
          <Route path="/dashboard" element={<Home />} />
          <Route path="/project" element={<ProjectDetail />} />
          <Route path="/projectdetails/:projectId" element={<ProjectDetails />} />
          <Route path="/project-history/:projectId" element={<ProjectHistory />} />
          <Route path="/campaigns" element={<CampaignDetails />} />
          <Route path="/campaigndetails/:campaignId" element={<CampaignDetailsProjects />} />
          <Route path="/campaignDetailsInfo/:projectId/:campaignId" element={<CampaignDetailsInfo />} />
          <Route path="/campaignprojecthistory/:campaignId" element={<CampaignAndProjectHistory />} />
          <Route path="/campaignprojecthistorydetails/:campaignId/:projectId" element={<CampaignAndProjectHistoryDetails />} />
          <Route path="/createcampaign" element={<CreateCampaign />} />
          <Route path="/addproject/:campaignName" element={<AddProject />} />
          <Route path="/adminreview/:campaignName" element={<AdminReview />} />
          <Route path="/functions" element={<Functions />} />
          <Route path="/createfunction" element={<CreateFunction />} />
          <Route path="/functioncampaigns" element={<FunctionCampaign />} />
          <Route path="/functioncampaigndetails/:campaignId" element={<FunctionCampaignDetails />} />
          <Route path="/functionCampaigndetailsinfo/:campaignId/:functionId" element={<FunctionCampaignDetailsInfo />} />
          <Route path="/addcampaignfunction/:campaignName" element={<AddCampaignFunctions />} />
          <Route path="/filters/:campaignId" element={<FilterPage />} />
          <Route path="/initialsetup" element={<InitialSetup />} />
          <Route path="/rolemapping" element={<RoleMapping />} />
          <Route path="/buildformula" element={<BuildFormula />} />
          <Route path="/bulkupload" element={<BulkUpload />} />
          <Route path="/mappedheaders" element={<MappedHeaders />} />
          <Route path="/bulkuploaddetails/:jobId" element={<BulkUploadDetails />} />
          <Route path="/usermanagement" element={<UserManagement />} />
          <Route path="/globaltemplates" element={<GlobalTemplates />} />
          <Route path="/globaltemplatesdetails/:localTemplateId" element={<GlobalTemplatesDetails />} />
          <Route path="/adminchecklists" element={<AdminCheckListManagement />} />
          <Route path="/adminchecklistdetails/:localTemplateId" element={<AdminChecklistDetails />} />
          <Route path="/adminviewchecklistdetails/:localTemplateId" element={<AdminViewChecklistDetails />} />
          <Route path="/auditorchecklists" element={<AuditorCheckListManagement />} />
          <Route path="/auditorchecklistdetails/:campaignId/:projectId" element={<AuditorChecklistDetails />} />
          <Route path="/auditorviewchecklistdetails/:campaignId/:projectId" element={<AuditorViewChecklistDetails />} />
          <Route path="/customizetheme" element={<CustomTheme />} />
          <Route path="/templates" element={<EmailTemplateUI />} />
          <Route path="/createtemplate/:clientId" element={<CreateTemplate />} />
          <Route path="/emailtemplatedetail/:clientId/:templateId" element={<EditTemplate />} />
          <Route path="/nctracker" element={<NCTracker />} />
          <Route path="/nctrackerdetails/:campaignName" element={<NCTrackerDetails />} />
          <Route path="/functionchecklist" element={<FunctionChecklist />} />
          <Route path="/functionchecklistdetails/:functionTemplateId" element={<FunctionCheckListDetails />} />
          <Route path="/functionviewchecklistdetails/:functionTemplateId" element={<FunctionViewChecklistDetails />} />
          <Route path="/globalfunctiontemplate" element={<GlobalFunctionTemplates />} />
          <Route path="/globalfunctiontemplatedetails/:functionTemplateId" element={<GlobalFunctionTemplateDetails />} />
          <Route path="/createfunctionCampaign" element={<CreateFunctionCampaign />} />
          <Route path="/auditorfunctionchecklists" element={<AuditorFunctionChecklist />} />
          <Route path="/auditorfunctionchecklistdetails/:campaignId/:functionId" element={<AuditorFunctionCheckListDetails />} />
          <Route path="/auditorviewfuntionchecklistdetails/:campaignId/:functionId" element={<AuditorViewFunctionCheckListDetails />} />
          <Route path="/functionnctracker" element={<FunctionNCTracker />} />
          <Route path="/functionnctrackerdetails/:campaignName" element={<FunctionNCTrackerDetails />} />
          <Route path="/addfunction" element={<AddFunctions />} />
          <Route path="/meetingtracker" element={<MeetingTracker />} />
          <Route path="/meetingtrackerdetails/:campaignId" element={<MeetingTrackerDetails />} />
          <Route path="/meetingtrackerdetailsinfo/:campaignId/:projectId" element={<MeetingTrackerDetailsInfo />} />
          <Route path="/functionmeetingtracker" element={<FunctionMeetingTracker />} />
          <Route path="/functionmeetingtrackerdetails/:campaignId" element={<FunctionMeetingTrackerDetails />} />
          <Route path="/functionmeetingtrackerdetailsinfo/:campaignId/:functionId" element={<FunctionMeetingTrackerDetailsInfo />} />
          <Route path="/excludedprojectdetails/:campaignId" element={<ExcludedCampaignAndProject />} />
          <Route path="/excludedcampaignprojectdetails/:campaignId/:projectId" element={<ExcludedCampaignAndProjectDetails />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>
    </div>
  );
};
export default App;